/**
 * Detects whether a SQL string is an executable statement that produces results.
 * Used by the Peek Results feature to show/hide the floating preview button.
 */

import type { DatabaseEngineId } from '@/core/engines';

/** Strip line comments (--) and block comments, respecting string literals */
function stripComments(sql: string): string {
  let result = '';
  let i = 0;
  while (i < sql.length) {
    // String literal
    if (sql[i] === "'") {
      result += sql[i++];
      while (i < sql.length && sql[i] !== "'") {
        if (sql[i] === "'" && sql[i + 1] === "'") {
          result += "''";
          i += 2;
        } else {
          result += sql[i++];
        }
      }
      if (i < sql.length) result += sql[i++]; // closing quote
    }
    // Block comment
    else if (sql[i] === '/' && sql[i + 1] === '*') {
      i += 2;
      while (i < sql.length && !(sql[i] === '*' && sql[i + 1] === '/')) i++;
      i += 2; // skip */
    }
    // Line comment
    else if (sql[i] === '-' && sql[i + 1] === '-') {
      while (i < sql.length && sql[i] !== '\n') i++;
    } else {
      result += sql[i++];
    }
  }
  return result;
}

/** Split SQL into statements by semicolons, respecting string literals */
// TODO(sql-grammar): migrate to DocumentStructure (src/features/sql-grammar/document)
// — the notebook peek-detection path should reuse getDocumentStructure(doc, id)
// once the Statement Classification slice wires classifyStatement into the runner.
function splitStatements(sql: string): string[] {
  const statements: string[] = [];
  let current = '';
  let i = 0;
  while (i < sql.length) {
    if (sql[i] === "'") {
      current += sql[i++];
      while (i < sql.length) {
        if (sql[i] === "'" && sql[i + 1] === "'") {
          current += "''";
          i += 2;
        } else if (sql[i] === "'") {
          current += sql[i++];
          break;
        } else {
          current += sql[i++];
        }
      }
    } else if (sql[i] === ';') {
      const trimmed = current.trim();
      if (trimmed) statements.push(trimmed);
      current = '';
      i++;
    } else {
      current += sql[i++];
    }
  }
  const trimmed = current.trim();
  if (trimmed) statements.push(trimmed);
  return statements;
}

/** Check if a trailing comma indicates an incomplete fragment */
function hasTrailingComma(sql: string): boolean {
  const stripped = stripComments(sql).trim();
  return stripped.endsWith(',');
}

/**
 * Safety allowlist: only read-only, result-producing statement forms are peekable.
 * Mutating DML (INSERT/UPDATE/DELETE — even with RETURNING/OUTPUT) and procedure
 * execution (EXEC/EXECUTE/CALL) are excluded because Peek runs the SQL silently
 * and the user may not realize a mutation occurred.
 */
const READ_ONLY_PREFIXES = /^(SELECT|WITH|TABLE|VALUES|EXPLAIN|SHOW|DESCRIBE|DESC)\b/i;
const DDL_DML_PREFIXES = /^(CREATE|ALTER|DROP|TRUNCATE|GRANT|REVOKE|USE|SET|BEGIN|COMMIT|ROLLBACK|MERGE|INSERT|UPDATE|DELETE|EXEC|EXECUTE|CALL)\b/i;
const INCOMPLETE_STARTS = /^(FROM|WHERE|HAVING|GROUP|ORDER|LIMIT|OFFSET|FETCH|AND|OR|ON|JOIN|INNER|LEFT|RIGHT|FULL|CROSS)\b/i;

/**
 * Any mutating / side-effecting keyword that must disqualify a statement
 * anywhere in its body — including inside CTEs. The first-token gate (above)
 * only catches `INSERT ...` / `DELETE ...` at the very start of the statement;
 * a WITH-CTE such as `WITH d AS (DELETE ... RETURNING *) SELECT * FROM d`
 * starts with `WITH`, bypasses the prefix gate, and would silently mutate.
 */
const MUTATION_KEYWORDS = /\b(INSERT|UPDATE|DELETE|MERGE|TRUNCATE|DROP|CREATE|ALTER|GRANT|REVOKE|EXEC|EXECUTE|CALL)\b/i;

/**
 * Prefixes whose statements are read-only metadata commands that cannot embed
 * mutating DML, and should therefore bypass the full-statement mutation scan.
 * `SHOW CREATE TABLE ...`, `SHOW CREATE VIEW ...`, `SHOW CREATE PROCEDURE ...`
 * (MySQL/MariaDB) contain the keyword `CREATE` even though the statement is
 * pure metadata — the scan would otherwise reject them. `DESCRIBE`/`DESC` are
 * likewise pure metadata. Anything starting with these tokens is safe to peek
 * without the defensive scan.
 *
 * NOTE: `EXPLAIN` is intentionally NOT in this list — `EXPLAIN (ANALYZE)
 * DELETE FROM users` in PostgreSQL actually executes the DELETE, so the scan
 * must stay active for EXPLAIN statements.
 */
const METADATA_ONLY_PREFIXES = /^(SHOW|DESCRIBE|DESC)\b/i;

/**
 * Replace string literals and quoted identifiers (single quotes, double quotes,
 * brackets, backticks) with spaces of equal length. Used to neutralize contents
 * before scanning for keywords so a column value like `'DELETE'` or an identifier
 * like `"insert"` can't trigger a false positive.
 */
function stripStringsAndQuotedIdents(sql: string): string {
  let result = '';
  let i = 0;
  while (i < sql.length) {
    const ch = sql[i];
    if (ch === "'") {
      result += ' ';
      i++;
      while (i < sql.length) {
        if (sql[i] === "'" && sql[i + 1] === "'") { result += '  '; i += 2; continue; }
        if (sql[i] === "'") { result += ' '; i++; break; }
        result += ' '; i++;
      }
    } else if (ch === '"') {
      result += ' '; i++;
      while (i < sql.length && sql[i] !== '"') { result += ' '; i++; }
      if (i < sql.length) { result += ' '; i++; }
    } else if (ch === '[') {
      result += ' '; i++;
      while (i < sql.length && sql[i] !== ']') { result += ' '; i++; }
      if (i < sql.length) { result += ' '; i++; }
    } else if (ch === '`') {
      result += ' '; i++;
      while (i < sql.length && sql[i] !== '`') { result += ' '; i++; }
      if (i < sql.length) { result += ' '; i++; }
    } else {
      result += ch;
      i++;
    }
  }
  return result;
}

export function isExecutableSelect(sql: string): boolean {
  if (!sql || !sql.trim()) return false;

  const stripped = stripComments(sql).trim();
  if (!stripped) return false;

  // Split into statements — must be exactly one
  const statements = splitStatements(stripped);
  if (statements.length !== 1) return false;

  const stmt = statements[0];

  // Check for trailing comma (incomplete fragment)
  if (hasTrailingComma(stmt)) return false;

  // Check for incomplete fragments (starts with a clause keyword, not a statement keyword)
  if (INCOMPLETE_STARTS.test(stmt)) return false;

  // DDL, DML, and procedure execution — never peekable (safety gate, first-token check)
  if (DDL_DML_PREFIXES.test(stmt)) return false;

  // Read-only result-producing statements only
  if (!READ_ONLY_PREFIXES.test(stmt)) return false;

  // Pure-metadata commands (SHOW / DESCRIBE / DESC) bypass the mutation scan.
  // `SHOW CREATE TABLE foo` contains the keyword CREATE but is read-only — the
  // scan would otherwise reject it. These commands have no body that could
  // embed DML, so the first-token allowlist is sufficient.
  if (METADATA_ONLY_PREFIXES.test(stmt)) return true;

  // Full-statement scan — a read-only outer form (SELECT, WITH, ...) must not
  // contain a mutating keyword anywhere in its body (outside strings / quoted
  // identifiers). This closes the WITH ... (DELETE RETURNING) SELECT loophole
  // and is intentionally stricter than strictly necessary: false negatives only
  // disable Peek (user can still click Execute), but false positives run silent
  // mutations.
  const scanned = stripStringsAndQuotedIdents(stmt);
  if (MUTATION_KEYWORDS.test(scanned)) return false;

  return true;
}

/**
 * Detects whether a SQL string looks like a complete, executable statement
 * (or a sequence of them) — i.e. something a user could reasonably run.
 *
 * This is the visibility gate for the floating "Execute selection" button.
 * Unlike `isExecutableSelect`, mutating DML (INSERT/UPDATE/DELETE), DDL, and
 * procedure calls are all allowed: running them is the whole point of the
 * button.
 *
 * Philosophy: "show the affordance, let execute fail on the server." The gate
 * only rejects selections that are **clearly** not runnable in any dialect —
 * clause-keyword fragments (`FROM users`, `WHERE x=1`) and trailing commas.
 * Bare identifiers, numeric literals, T-SQL implicit-EXEC proc calls
 * (`MyProc 123`), and `GO` batch separators all pass: the bar appears, and
 * clicking Execute either runs them or surfaces a SQL error. Hiding the bar
 * for anything that "looks garbled" was causing valid calls (implicit EXEC,
 * GO-separated batches) to silently fail the visibility check.
 */
export function isExecutableStatement(sql: string): boolean {
  if (!sql || !sql.trim()) return false;

  const stripped = stripComments(sql).trim();
  if (!stripped) return false;

  const statements = splitStatements(stripped);
  if (statements.length === 0) return false;

  for (const stmt of statements) {
    const trimmed = stmt.trim();
    if (!trimmed) return false;
    if (INCOMPLETE_STARTS.test(trimmed)) return false;
    if (hasTrailingComma(trimmed)) return false;
  }

  return true;
}

/**
 * Keywords that, if they appear as the LAST token of a statement, signal the
 * statement is mid-composition (still expecting more text to follow). Engine
 * extensions layer additional tokens on top of this base set.
 */
const TRAILING_CONTINUATION_BASE = new Set<string>([
  'FROM', 'WHERE', 'HAVING', 'GROUP', 'ORDER', 'BY', 'LIMIT', 'OFFSET',
  'FETCH', 'FIRST', 'ROWS', 'ONLY', 'UNION', 'INTERSECT', 'EXCEPT',
  'JOIN', 'INNER', 'LEFT', 'RIGHT', 'FULL', 'CROSS', 'OUTER', 'ON',
  'AND', 'OR', 'NOT', 'IN', 'LIKE', 'ILIKE', 'BETWEEN', 'IS', 'NULL',
  'INTO', 'VALUES', 'SET', 'AS', 'USING', 'OVER',
  'CASE', 'WHEN', 'THEN', 'ELSE',
  'WITH',
  'DISTINCT', 'ALL', 'TOP',
  'EXISTS', 'ANY', 'SOME',
  // Transaction / DDL scaffolding
  'TRANSACTION', 'TRAN', 'SAVEPOINT',
]);

const TRAILING_CONTINUATION_BY_ENGINE: Partial<Record<DatabaseEngineId, string[]>> = {
  // T-SQL keywords that commonly trail an in-progress statement.
  mssql: ['OUTPUT', 'PIVOT', 'UNPIVOT', 'OPTION'],
  // PL/pgSQL / pgsql-specific clauses that require a following body/value.
  pgsql: ['RETURNING', 'CONFLICT', 'DO', 'LATERAL'],
  mysql: ['STRAIGHT_JOIN'],
  oracle: ['PARTITION', 'CONNECT', 'PRIOR'],
  sqlite: [],
  mock: [],
};

/**
 * Clause forms that legitimately END a statement even though their last token
 * is in `TRAILING_CONTINUATION_BASE`. Those keywords are ambiguous: `ONLY`,
 * `ROWS` and `NULL` each appear both mid-composition (`FETCH FIRST`, `OFFSET`,
 * `IS`) and as the natural terminator of a finished clause. Matching the whole
 * clause — not just the last word — tells the two apart, so
 * `... FETCH FIRST 200 ROWS ONLY` is recognized as a runnable statement while
 * `... FETCH FIRST` is still treated as mid-typing.
 */
const COMPLETE_CLAUSE_TAILS: RegExp[] = [
  // Oracle / MSSQL / DB2 / pgsql row-limiting: `FETCH FIRST|NEXT [n] ROW[S] ONLY|WITH TIES`
  /\bFETCH\s+(?:FIRST|NEXT)\s+(?:[^\s()]+\s+)?ROWS?\s+(?:ONLY|WITH\s+TIES)$/i,
  // `OFFSET n ROW[S]` (complete on its own — the FETCH clause is optional)
  /\bOFFSET\s+[^\s()]+\s+ROWS?$/i,
  // `IS [NOT] NULL`
  /\bIS\s+(?:NOT\s+)?NULL$/i,
];

/** Trailing punctuation/operators that indicate an incomplete statement. */
const TRAILING_OPERATOR_PATTERN = /(?:<=|>=|<>|!=|::|\|\||[=<>+\-*/%,(])$/;

/**
 * Lightweight, engine-aware tokenizer that mirrors the parser in
 * `statementAtCursor.ts`. Strings, comments, and quoted identifiers are
 * "neutralized" so the trailing-operator / paren-balance scan can't be
 * confused by their contents.
 *
 * Chars inside strings and quoted identifiers are replaced with the
 * alphanumeric placeholder `x` (same length as the original content) rather
 * than a space. Using `x` preserves the fact that *something* occupied that
 * position, so patterns like `WHERE x = 'REG'` don't get neutralized into
 * `WHERE x =      ` — which would expose the `=` to the trailing-operator
 * check and mark an otherwise complete statement as incomplete. Comments
 * remain blanked with spaces since they're considered true whitespace.
 */
function neutralizeForScan(sql: string, engineId?: DatabaseEngineId): string {
  const isPg = engineId === 'pgsql';
  let out = '';
  let i = 0;
  while (i < sql.length) {
    const ch = sql[i];
    const next = sql[i + 1];

    if (ch === '-' && next === '-') {
      while (i < sql.length && sql[i] !== '\n') { out += ' '; i++; }
      continue;
    }
    if (ch === '/' && next === '*') {
      if (isPg) {
        let depth = 1;
        out += '  '; i += 2;
        while (i < sql.length && depth > 0) {
          if (sql[i] === '/' && sql[i + 1] === '*') { depth++; out += '  '; i += 2; continue; }
          if (sql[i] === '*' && sql[i + 1] === '/') { depth--; out += '  '; i += 2; continue; }
          out += ' '; i++;
        }
      } else {
        out += '  '; i += 2;
        while (i < sql.length && !(sql[i] === '*' && sql[i + 1] === '/')) { out += ' '; i++; }
        if (i < sql.length) { out += '  '; i += 2; }
      }
      continue;
    }
    if (isPg && ch === '$') {
      const delim = readDollarDelimiter(sql, i);
      if (delim) {
        out += 'x'.repeat(delim.length); i += delim.length;
        while (i < sql.length && !sql.startsWith(delim, i)) { out += 'x'; i++; }
        if (i < sql.length) { out += 'x'.repeat(delim.length); i += delim.length; }
        continue;
      }
    }
    if (ch === "'") {
      out += 'x'; i++;
      while (i < sql.length) {
        if (sql[i] === "'" && sql[i + 1] === "'") { out += 'xx'; i += 2; continue; }
        if (sql[i] === "'") { out += 'x'; i++; break; }
        out += 'x'; i++;
      }
      continue;
    }
    if (ch === '"') {
      out += 'x'; i++;
      while (i < sql.length && sql[i] !== '"') { out += 'x'; i++; }
      if (i < sql.length) { out += 'x'; i++; }
      continue;
    }
    if (ch === '[') {
      out += 'x'; i++;
      while (i < sql.length && sql[i] !== ']') { out += 'x'; i++; }
      if (i < sql.length) { out += 'x'; i++; }
      continue;
    }
    if (ch === '`') {
      out += 'x'; i++;
      while (i < sql.length && sql[i] !== '`') { out += 'x'; i++; }
      if (i < sql.length) { out += 'x'; i++; }
      continue;
    }
    out += ch;
    i++;
  }
  return out;
}

function readDollarDelimiter(sql: string, startIndex: number): string | null {
  if (sql[startIndex] !== '$') return null;
  let i = startIndex + 1;
  while (i < sql.length && /[A-Za-z0-9_]/.test(sql[i])) i++;
  if (i < sql.length && sql[i] === '$') return sql.slice(startIndex, i + 1);
  return null;
}

/**
 * A handful of words in `TRAILING_CONTINUATION_BASE` are only *sometimes*
 * mid-composition markers — whether they terminate a statement depends on the
 * token(s) before them. Resolving them by look-back (instead of flat set
 * membership) keeps the pencil / Monaco highlight / Execute|Peek bar on
 * extremely common complete endings that the flat check wrongly rejected
 * (BUG 4 / SEL-01 / SEL-06 / DML-02):
 *
 * - `NULL` — a trailing literal `NULL` always completes an expression
 *   (`… IS NULL`, `… IS NOT NULL`, `SET x = NULL`, `SELECT NULL`). The
 *   incomplete forms surface as a trailing operator (`x =`, `a,`), which
 *   `TRAILING_OPERATOR_PATTERN` already rejects — so `NULL` as the final token
 *   is terminal.
 * - `ONLY` — terminal in `FETCH FIRST/NEXT n ROW[S] ONLY`; otherwise the
 *   statement is still being composed.
 * - `ROWS` — terminal in the T-SQL `OFFSET <n> ROWS` form; still
 *   mid-composition in `FETCH FIRST/NEXT <n> ROWS` (needs `ONLY` / `WITH TIES`).
 *
 * `tail` is the neutralized, trimmed statement text (strings/comments already
 * blanked), so tokenizing it can't be fooled by literal contents.
 */
function isContextTerminalKeyword(lastWord: string, tail: string): boolean {
  if (lastWord !== 'NULL' && lastWord !== 'ONLY' && lastWord !== 'ROWS') return false;
  if (lastWord === 'NULL') return true;

  const tokens = tail.toUpperCase().match(/[A-Z_][A-Z0-9_]*|\d+/g) ?? [];
  const n = tokens.length;
  const prev = n >= 2 ? tokens[n - 2] : undefined;
  const prev2 = n >= 3 ? tokens[n - 3] : undefined;

  if (lastWord === 'ONLY') {
    return prev === 'ROWS' || prev === 'ROW';
  }
  // lastWord === 'ROWS'
  return prev2 === 'OFFSET';
}

/**
 * Stricter than `isExecutableStatement` — additionally rejects statements
 * that look mid-composition: unbalanced parens, trailing operators, or
 * trailing continuation keywords. Used by the "current statement" cursor
 * feature to avoid flashing the floating Execute|Peek bar on every keystroke
 * while the user is still typing a statement.
 *
 * Engine-aware: the continuation-keyword set is extended per dialect (e.g.,
 * `RETURNING` / `OVER` for pgsql, `OUTPUT` / `PIVOT` / `OPTION` for mssql).
 * Dollar-quoted strings and nested block comments are ignored when scanning
 * for trailing tokens in pgsql.
 */
export function isLikelyCompleteStatement(
  sql: string,
  engineId?: DatabaseEngineId
): boolean {
  if (!isExecutableStatement(sql)) return false;
  const stripped = stripComments(sql).trim();
  if (!stripped) return false;

  const noTrailingSemis = stripped.replace(/;+\s*$/, '').trim();
  if (!noTrailingSemis) return false;

  // Balanced parentheses — operate on the string with quoted regions blanked
  // out so `'('` inside a string literal doesn't throw the count off.
  const neutralized = neutralizeForScan(noTrailingSemis, engineId);
  let depth = 0;
  for (let i = 0; i < neutralized.length; i++) {
    const ch = neutralized[i];
    if (ch === '(') depth++;
    else if (ch === ')') { depth--; if (depth < 0) return false; }
  }
  if (depth !== 0) return false;

  // Trailing operator / punctuation (check against neutralized tail so a
  // trailing `'` inside a string isn't treated as an operator).
  const tail = neutralized.trimEnd();
  if (TRAILING_OPERATOR_PATTERN.test(tail)) return false;

  // Trailing continuation keyword — unless the tail completes a clause whose
  // last token is legitimately one of those keywords (`... ROWS ONLY`,
  // `... OFFSET 20 ROWS`, `... IS NOT NULL`). Anything that slips past this
  // regex gate still gets the per-keyword look-back below: a few members of
  // the base set are only context-sensitively terminal (`NULL` / `ONLY` /
  // `ROWS`), and resolving those keeps common complete endings' pencil /
  // highlight / Execute|Peek bar (BUG 4 / SEL-01 / SEL-06 / DML-02).
  const lastWordMatch = COMPLETE_CLAUSE_TAILS.some((re) => re.test(tail))
    ? null
    : tail.match(/([A-Za-z_][A-Za-z0-9_]*)\s*$/);
  if (lastWordMatch) {
    const lastWord = lastWordMatch[1].toUpperCase();
    if (!isContextTerminalKeyword(lastWord, tail)) {
      if (TRAILING_CONTINUATION_BASE.has(lastWord)) return false;
      const engineExtras = engineId ? TRAILING_CONTINUATION_BY_ENGINE[engineId] : undefined;
      if (engineExtras && engineExtras.includes(lastWord)) return false;
    }
  }

  return true;
}

/**
 * Pencil-reachability predicate — the "distinct predicate" that decides whether
 * the Compact VQE edit pencil may light up for the statement under the cursor,
 * SPLIT from the Execute/Peek bar's flash-suppression gate
 * (`isLikelyCompleteStatement`).
 *
 * Why split: the pencil is driven by the *real parse* — `classifyAtCursor`
 * (via `useCursorStatementEditInfo`) re-classifies the statement and only
 * returns an editable target for SELECT/DML/DDL. So the pencil must NOT inherit
 * the bar's trailing-continuation-keyword heuristic: before this split, a
 * complete statement whose last word merely *coincided* with a continuation
 * keyword still in `TRAILING_CONTINUATION_BASE` had its pencil silently removed.
 * The bar keeps the stricter gate (suppress the run-action flash while typing a
 * trailing clause); the pencil only needs to know a real, structurally-sane
 * statement is under the cursor and lets the classifier decide the rest.
 *
 * Keeps ONLY the structural sanity gates — executable shape (rejects clause
 * fragments / trailing commas), balanced parens, no trailing operator — so
 * obvious garbage still gets no pencil. Deliberately omits the
 * trailing-continuation-keyword rejection, which is exactly what
 * `isLikelyCompleteStatement` layers on top for the bar.
 */
export function isPencilReachableStatement(
  sql: string,
  engineId?: DatabaseEngineId
): boolean {
  if (!isExecutableStatement(sql)) return false;
  const stripped = stripComments(sql).trim();
  if (!stripped) return false;

  const noTrailingSemis = stripped.replace(/;+\s*$/, '').trim();
  if (!noTrailingSemis) return false;

  // Balanced parentheses (quoted regions blanked so `'('` in a string doesn't
  // throw the count off) — same neutralizer the bar gate uses.
  const neutralized = neutralizeForScan(noTrailingSemis, engineId);
  let depth = 0;
  for (let i = 0; i < neutralized.length; i++) {
    const ch = neutralized[i];
    if (ch === '(') depth++;
    else if (ch === ')') { depth--; if (depth < 0) return false; }
  }
  if (depth !== 0) return false;

  // A trailing operator / punctuation means the expression is unfinished — no
  // pencil (the classifier would have nothing coherent to open).
  const tail = neutralized.trimEnd();
  if (TRAILING_OPERATOR_PATTERN.test(tail)) return false;

  return true;
}

/** Check if SQL already contains a row-limiting clause */
const HAS_TOP = /\bTOP\s*[\d(]/i;
const HAS_LIMIT = /\bLIMIT\s+\d/i;
const HAS_FETCH_FIRST = /\bFETCH\s+FIRST\b/i;

/** Find the outer SELECT keyword in a query, skipping CTE preambles */
function findOuterSelect(sql: string): number {
  const upper = sql.toUpperCase();
  // For WITH ... SELECT, find the SELECT after the CTE closing paren
  if (/^\s*WITH\b/i.test(sql)) {
    let depth = 0;
    let inString = false;
    for (let i = 0; i < sql.length; i++) {
      if (sql[i] === "'" && !inString) { inString = true; continue; }
      if (sql[i] === "'" && inString) { inString = false; continue; }
      if (inString) continue;
      if (sql[i] === '(') depth++;
      if (sql[i] === ')') depth--;
      if (depth === 0 && upper.substring(i).match(/^\s*\)\s*SELECT\b/i)) {
        return upper.indexOf('SELECT', i + 1);
      }
    }
  }
  // Simple SELECT
  const m = upper.match(/^\s*SELECT\b/);
  return m ? upper.indexOf('SELECT') : -1;
}

/**
 * Wrap SQL with a row limit if it doesn't already have one.
 * Engine-aware: uses TOP N for MSSQL, LIMIT N for PostgreSQL/MySQL/SQLite, FETCH FIRST N ROWS ONLY for Oracle.
 */
export function wrapWithLimit(sql: string, engineId: string, limit: number): string {
  // Strip trailing semicolons
  const s = sql.replace(/;\s*$/, '');

  // Only wrap SELECT (and WITH...SELECT) statements
  const stripped = stripComments(s).trim();
  if (!/^\s*(SELECT|WITH)\b/i.test(stripped)) return s;

  // Check if already limited
  if (HAS_TOP.test(stripped) || HAS_LIMIT.test(stripped) || HAS_FETCH_FIRST.test(stripped)) return s;

  if (engineId === 'mssql') {
    // Insert TOP N after SELECT [DISTINCT|ALL]
    const selectIdx = findOuterSelect(s);
    if (selectIdx === -1) return s;
    const afterSelect = selectIdx + 6; // length of "SELECT"
    const rest = s.substring(afterSelect);
    const distinctMatch = rest.match(/^(\s+(?:DISTINCT|ALL))\b/i);
    const insertAt = afterSelect + (distinctMatch ? distinctMatch[1].length : 0);
    return s.substring(0, insertAt) + ` TOP ${limit}` + s.substring(insertAt);
  }

  if (engineId === 'oracle') {
    return `${s} FETCH FIRST ${limit} ROWS ONLY`;
  }

  // PostgreSQL, MySQL, SQLite, and others
  return `${s} LIMIT ${limit}`;
}
